import { inject } from '@angular/core';
import {
  patchState,
  signalStoreFeature,
  type SignalStoreFeature,
  type SignalStoreFeatureResult,
  withMethods,
  withState,
} from '@ngrx/signals';

import { BILLING_CHARGES_DEFAULT_ORDERING } from './charges-default-ordering.token';
import { pickerToDay } from '@dgacontrol-frontend/shared-util-dates';

/**
 * State + setters shared by the 3 billing-charges list stores.
 *
 * DECISION — the feature owns STATE, not orchestration: the setters mutate
 * the slice but do NOT call `store.load()`. The consumer component decides
 * when to apply, and delegates the silent-reset to `withCursorPagination.load()`
 * which already bumps `requestVersion`, cancels the in-flight query via
 * `switchMap` and calls `setAllEntities`. Wiring `load()` here would force
 * every consumer to inherit the same "apply on change" UX; both the admin
 * drawer and the web drawer apply on the "Aplicar" button, so leaving the
 * trigger to the component keeps both UXs consistent.
 *
 * State holds `Date | null` (not string) because `p-datepicker [ngModel]`
 * wants a `Date`; `buildChargesFiltersQuery()` converts to civil-day ISO at
 * the boundary. Local getters avoid the UTC roll-back that
 * `toISOString().slice(0,10)` triggers for timezones west of UTC.
 *
 * `buildChargesFiltersQuery()` is a plain method — not a `withComputed`
 * signal — because it is only ever called imperatively at the moment the
 * component builds the next `store.load(...)` payload. A `computed` would
 * pin one extra dependency graph edge with no reactive consumer.
 *
 * The explicit `SignalStoreFeature<Input, Output>` return type is REQUIRED,
 * not cosmetic: a generic feature whose output type is INFERRED collapses
 * to an index signature, so the consumer's `store.dateOrder()` trips TS4111
 * under `noPropertyAccessFromIndexSignature` (active in this repo's lib +
 * app tsconfigs). Annotating the output with literal method + state keys
 * keeps them named at every call site — the same pattern used by
 * `withCursorPagination` in `libs/shared/util/cursor-pagination`.
 */
export interface ChargesListFiltersState {
  dateOrder: 'asc' | 'desc';
  fromDate: Date | null;
  toDate: Date | null;
}

export interface ChargesFiltersQuery {
  date_order: 'asc' | 'desc';
  from_date?: string;
  to_date?: string;
}

export function withChargesListFilters<
  Input extends SignalStoreFeatureResult,
>(): SignalStoreFeature<
  Input,
  {
    state: ChargesListFiltersState;
    props: Record<string, never>;
    methods: {
      toggleDateOrder: () => void;
      setFromDate: (v: Date | null) => void;
      setToDate: (v: Date | null) => void;
      clearDateRange: () => void;
      buildChargesFiltersQuery: () => ChargesFiltersQuery;
    };
  }
> {
  return signalStoreFeature(
    withState<ChargesListFiltersState>(() => ({
      dateOrder: inject(BILLING_CHARGES_DEFAULT_ORDERING),
      fromDate: null,
      toDate: null,
    })),
    withMethods((store) => ({
      toggleDateOrder(): void {
        patchState(store, {
          dateOrder: store.dateOrder() === 'asc' ? 'desc' : 'asc',
        });
      },
      setFromDate(v: Date | null): void {
        patchState(store, { fromDate: v });
      },
      setToDate(v: Date | null): void {
        patchState(store, { toDate: v });
      },
      clearDateRange(): void {
        patchState(store, { fromDate: null, toDate: null });
      },
      buildChargesFiltersQuery(): ChargesFiltersQuery {
        const q: ChargesFiltersQuery = { date_order: store.dateOrder() };
        const f = store.fromDate();
        if (f) q.from_date = pickerToDay(f);
        const t = store.toDate();
        if (t) q.to_date = pickerToDay(t);
        return q;
      },
    })),
  );
}
